import React, { useState, useEffect } from "react";
import { Dropdown, Form } from "react-bootstrap";
import "./MultiSelectDropdown.css";

// This is a multi selector dropdown (RESUABLE)
// options => expected data is an array of objects in label, value format
// onSelectItemChange => function to be called on selection change and will return selected keyName
// valueName => display text and keyName => passed to server text
// dropdownName => dropdown name for selected value function
// toggleClass,menuClass => dropdown toggle and menu css classes
// itemLabel => singular noun for the toggle title ("1 collection selected", "2 collections selected"), default "item"
// selectedValues => optional pre-selected items (same shape as options), kept in sync via useEffect
//   so a parent-seeded default selection isn't silently dropped the first time the user checks a box

// eg => options = [{keyName: '1', valueName: 'option 1'}, {keyName: '2', valueName: 'option 2'}] returns keyName: '1'

const EMPTY_SELECTION = [];

const MultiSelectDropdown = ({
  options = [],
  onSelectionChange,
  dropdownName,
  placeholder = "Select items",
  toggleClass,
  menuClass,
  keyName,
  valueName,
  selectedValues = EMPTY_SELECTION,
  itemLabel = "item"
}) => {
  const [selectedItems, setSelectedItems] = useState(selectedValues);

  useEffect(() => {
    setSelectedItems(selectedValues);
  }, [selectedValues]);

  const getNestedValue = (object, path) =>
    path.split('.').reduce((acc, key) => acc?.[key], object);

  const handleCheckboxChange = (event) => {
    const itemId = event.target.id;
    const selectedOption = options.find((option) => option[keyName] === itemId);
    let updatedSelectedItems;
    if (event.target.checked) {
      updatedSelectedItems = [...selectedItems, selectedOption];
    } else {
      updatedSelectedItems = selectedItems.filter(
        (item) => item[keyName] !== itemId
      );
    }
    setSelectedItems(updatedSelectedItems);
    onSelectionChange(updatedSelectedItems);
  };

  const handleSelectAllChange = (event) => {
    let updatedSelectedItems;
    if (event.target.checked) {
      updatedSelectedItems = [...options];
    } else {
      updatedSelectedItems = [];
    }
    setSelectedItems(updatedSelectedItems);
    onSelectionChange(updatedSelectedItems);
  };

  const getDropdownTitle = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    } else if (selectedItems.length === 1) {
      return `1 ${itemLabel} selected`;
    } else {
      return `${selectedItems.length} ${itemLabel}s selected`;
    }
  };

  const allSelected = selectedItems.length === options.length;

  return (
    <Dropdown
      data-bs-theme="dark"
      autoClose="outside"
      className={`reactDropdownRootClass halfWidth contents`}
      name={dropdownName}
    >
      <Dropdown.Toggle
        variant="secondary"
        id={`multi-dropdown-${dropdownName}`}
        className={`dropdown-display-class halfWidth ${toggleClass || ''}`}
      >
        {getDropdownTitle()}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className={`reactDropdownMenuClass halfWidth dropdown-menu-class ${menuClass || ''}`}
      >
        <Dropdown.Item as="div">
          <Form.Check
            type="checkbox"
            id="selectAll"
            label="Select All"
            onChange={handleSelectAllChange}
            checked={allSelected}
          />
        </Dropdown.Item>
        {options.map((option) => (
          <Dropdown.Item
            key={option[keyName]}
            as="div"
            className="dropdown-item-class"
          >
            <Form.Check
              type="checkbox"
              id={option[keyName]}
              label={getNestedValue(option, valueName)}
              onChange={handleCheckboxChange}
              checked={selectedItems.some(
                (item) => item[keyName] === option[keyName]
              )}
            />
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MultiSelectDropdown;
